import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, resolveImg, formatApiErrorDetail } from "../lib/api";
import { Sparkles, Eye, EyeOff } from "lucide-react";

function TemplateCollage() {
  const [imgs, setImgs] = useState([]);
  useEffect(() => {
    api.get("/templates").then(({ data }) => {
      const seen = new Set();
      const picks = [];
      for (const t of data) {
        if (t.image_url && !seen.has(t.category)) {
          seen.add(t.category);
          picks.push(t);
        }
        if (picks.length >= 6) break;
      }
      setImgs(picks);
    }).catch(() => {});
  }, []);
  if (!imgs.length) return null;
  const rot = ["-3deg", "2deg", "-2deg", "3deg", "-2deg", "2deg"];
  return (
    <div className="grid grid-cols-3 gap-3 mt-8" data-testid="auth-collage">
      {imgs.map((t, i) => (
        <div key={t.id} className="nb-card overflow-hidden bg-white transition-transform hover:rotate-0 hover:-translate-y-1" style={{ transform: `rotate(${rot[i]})` }}>
          <img src={resolveImg(t.image_url)} alt={t.title} loading="lazy" className="w-full h-24 object-cover" />
        </div>
      ))}
    </div>
  );
}

export default function Auth({ mode }) {
  const isSignup = mode === "signup";
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirect = location.state?.redirect || "/templates";

  const [form, setForm] = useState({ name: "", email: "", password: "", city: "", state: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (isSignup) {
        await register(form);
      } else {
        await login(form.email, form.password);
      }
      navigate(redirect);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Visual side */}
      <div className="hidden lg:flex flex-col justify-between p-12 bg-[#FFB5A7] border-r-2 border-[#0A0A0A]">
        <Link to="/" className="flex items-center gap-2 font-display font-bold text-2xl">
          <span className="grid place-items-center w-10 h-10 rounded-xl bg-white border-2 border-[#0A0A0A]"><Sparkles className="w-5 h-5" /></span>
          SendOccasions
        </Link>
        <div>
          <h2 className="text-4xl font-display font-bold leading-tight">Spread joy in seconds.</h2>
          <p className="text-[#0A0A0A]/80 mt-4 text-lg">Free greeting cards with your own photos, sent anywhere.</p>
          <TemplateCollage />
        </div>
        <div className="nb-card bg-white px-5 py-4 rotate-[-2deg] w-fit">🎉 Over a million smiles delivered</div>
      </div>

      {/* Form side */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="auth-form">
          <Link to="/" className="lg:hidden flex items-center gap-2 font-display font-bold text-xl mb-8">
            <Sparkles className="w-6 h-6" /> SendOccasions
          </Link>
          <h1 className="text-3xl sm:text-4xl font-display font-bold">{isSignup ? "Create your account" : "Welcome back"}</h1>
          <p className="text-[#4A4A4A] mt-2">{isSignup ? "It's free — start sending greetings." : "Log in to keep the smiles going."}</p>

          {error && (
            <div className="nb-border rounded-xl bg-[#FFD5CC] px-4 py-3 mt-5 font-semibold text-sm" data-testid="auth-error">{error}</div>
          )}

          <div className="space-y-4 mt-6">
            {isSignup && (
              <Field label="Your name" testid="auth-name">
                <input required value={form.name} onChange={set("name")} data-testid="auth-name-input"
                  className="w-full nb-border rounded-xl px-4 py-3 bg-[#FDFBF7] focus:outline-none focus:ring-2 focus:ring-[#A8E6CF]" placeholder="Jane Doe" />
              </Field>
            )}
            <Field label={isSignup ? "Email" : "Email or username"} testid="auth-email">
              <input required type={isSignup ? "email" : "text"} value={form.email} onChange={set("email")} data-testid="auth-email-input"
                className="w-full nb-border rounded-xl px-4 py-3 bg-[#FDFBF7] focus:outline-none focus:ring-2 focus:ring-[#A8E6CF]" placeholder={isSignup ? "you@email.com" : "you@email.com or username"} />
            </Field>
            <Field label="Password" testid="auth-password">
              <div className="relative">
                <input required type={showPw ? "text" : "password"} value={form.password} onChange={set("password")} data-testid="auth-password-input"
                  className="w-full nb-border rounded-xl px-4 py-3 pr-12 bg-[#FDFBF7] focus:outline-none focus:ring-2 focus:ring-[#A8E6CF]" placeholder="••••••••" />
                <button type="button" onClick={() => setShowPw(!showPw)} data-testid="toggle-password"
                  aria-label={showPw ? "Hide password" : "Show password"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4A4A4A] hover:text-[#0A0A0A]">
                  {showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </Field>
            <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer select-none -mt-2">
              <input type="checkbox" checked={showPw} onChange={() => setShowPw(!showPw)} data-testid="show-password-checkbox" className="w-4 h-4 accent-[#FFB5A7]" />
              Show password
            </label>
            {!isSignup && (
              <div className="text-right -mt-1">
                <Link to="/forgot-password" data-testid="forgot-link" className="text-sm font-semibold underline">Forgot password?</Link>
              </div>
            )}
            {isSignup && (
              <div className="grid grid-cols-2 gap-4">
                <Field label="City (optional)" testid="auth-city">
                  <input value={form.city} onChange={set("city")} data-testid="auth-city-input"
                    className="w-full nb-border rounded-xl px-4 py-3 bg-[#FDFBF7] focus:outline-none focus:ring-2 focus:ring-[#A8E6CF]" placeholder="Austin" />
                </Field>
                <Field label="State (optional)" testid="auth-state">
                  <input value={form.state} onChange={set("state")} data-testid="auth-state-input"
                    className="w-full nb-border rounded-xl px-4 py-3 bg-[#FDFBF7] focus:outline-none focus:ring-2 focus:ring-[#A8E6CF]" placeholder="Texas" />
                </Field>
              </div>
            )}
          </div>

          <button type="submit" disabled={loading} data-testid="auth-submit" className="nb-btn bg-[#FFB5A7] w-full py-3.5 mt-7 text-lg">
            {loading ? "Please wait…" : isSignup ? "Sign up free" : "Log in"}
          </button>

          {isSignup && (
            <p className="text-center text-xs text-[#4A4A4A] mt-3">
              By signing up you agree to our{" "}
              <Link to="/terms" className="font-bold underline">Terms</Link> and{" "}
              <Link to="/privacy" className="font-bold underline">Privacy Policy</Link>.
            </p>
          )}

          <p className="text-center text-[#4A4A4A] mt-6">
            {isSignup ? "Already have an account? " : "New here? "}
            <Link to={isSignup ? "/login" : "/signup"} state={{ redirect }} className="font-bold underline" data-testid="auth-switch">
              {isSignup ? "Log in" : "Sign up free"}
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="text-xs font-bold uppercase tracking-widest text-[#0A0A0A]">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
